import type { ERROR_CODE } from '@core/utils';

import type {
  EASY_LOGIN_USAGE_CODE,
  LOGIN_TYPE,
  POLICY_CASE_TYPE,
  TERM_CATEGORY,
  VERIFICATION_SERVICE_TYPE,
} from '@/api';
import type { GiftProductSortType } from '@/app/donation/gifts/types';
import type { HometownBusinessSortType } from '@/app/donation/hometown-business/types';
import type { GiftReviewSortType, InquiryType } from '@/app/donation/types';
import type { OsType } from '@/stores/DeviceStore';
import type {
  EasyCertificationParams,
  FAMILY_PROFILE_UPDATE_PAGE_TYPE,
} from '@/types/tabs';
import { createRoute } from '@/utils/createRoute';

export const ROUTES = {
  TEST: createRoute('/test'),

  GATEWAY: createRoute('/gateway'),

  POC: createRoute('/poc', { hasBottomNav: false }),

  APP_DOWNLOAD: createRoute('/app-download'),

  HOME: createRoute('/', {
    hasBottomNav: true,
  }),

  LOGIN: createRoute<{
    searchParams: {
      logout?: boolean;
      redirect?: string;
      disableOutlink?: boolean;
      memberCode?: string;
    };
  }>('/login'),

  LOGIN_POINT: createRoute('/login/point'),

  PEEC_EVENT: createRoute('/event/peec'),

  PEEC_APPLY: createRoute('/event/peec/apply'),

  LOCALFESTA: createRoute('/event/localfesta'),
  LOCALFESTA_APPLY: createRoute('/event/localfesta/apply'),

  AUTH: createRoute<{
    searchParams: {
      'login-type'?: LOGIN_TYPE;
      'social-login-token'?: string;
      code?: string;
      state?: string;
    };
  }>('/login/auth'),

  COMMUNITY: createRoute('/community', {
    hasBottomNav: true,
  }),
  COMMUNITY_CATEGORY: createRoute<{
    dynamicPath: { category: string; idIdx?: string };
  }>(
    ({ category, idIdx }) =>
      `/community/${category}${idIdx ? `/${idIdx}` : ''}`,
    {
      hasBottomNav: true,
    },
  ),
  WELLO_NEWS_DETAIL: createRoute<{
    dynamicPath: { idIdx: string };
  }>(({ idIdx }) => `/community/welloNews/${idIdx}`),
  HOMETOWN_NEWS_DETAIL: createRoute<{
    dynamicPath: { idIdx: string };
  }>(({ idIdx }) => `/community/hometownNews/${idIdx}`),
  POLICY_INFO_DETAIL: createRoute<{
    dynamicPath: { idIdx: string };
  }>(({ idIdx }) => `/community/policyInfo/${idIdx}`),

  POLICY_TALK_POST_EDIT: createRoute<{
    dynamicPath: {
      idx: string;
    };
  }>(({ idx }) => `/community/policyTalk/posting/${idx}`),
  POLICY_TALK_POST_NEW: createRoute<{
    searchParams: {
      code?: string;
      idx?: string;
      detailApplyType?: string;
    };
  }>('/community/policyTalk/posting/new'),
  FREE_TALK_POST_EDIT: createRoute<{
    dynamicPath: {
      idx: string;
    };
  }>(({ idx }) => `/community/freeTalk/posting/${idx}`),
  FREE_TALK_POST_NEW: createRoute('/community/freeTalk/posting/new'),

  MY: createRoute<{
    searchParams: {
      myPage?: string;
    };
  }>('/my', { hasBottomNav: true }),

  LUCKY_BALL: createRoute('/lucky-ball'),

  LUCKY_BALL_GUIDE: createRoute('/lucky-ball/guide'),

  MY_LUCKY_BALL: createRoute('/my/lucky-ball'),

  MY_PRODUCT_COUPONS: createRoute('/my/product-coupons'),

  MY_LUCKY_BALL_DETAIL: createRoute<{
    dynamicPath: {
      id: number;
    };
  }>(({ id }) => `/my/lucky-ball/${id}`),

  MY_SCRAP: createRoute('/my/scrap', {
    hasBottomNav: true,
  }),

  MY_REVIEWS: createRoute('/my/reviews'),

  MY_COMMUNITY_POSTED: createRoute('/my/community/posted', {
    hasBottomNav: true,
  }),
  MY_COMMUNITY_COMMENTED: createRoute('/my/community/commented', {
    hasBottomNav: true,
  }),

  MY_APPLY: createRoute('/my/apply', {
    hasBottomNav: true,
  }),

  MY_APPLY_RECOMMEND_INQUIRY: createRoute('/my/apply/recommend-inquiry'),

  /** @name 로컬마켓 주문내역 */
  MY_ORDERS: createRoute('/my/orders'),
  MY_ORDERS_DETAIL: createRoute<{
    dynamicPath: { orderNo: string };
  }>(({ orderNo }) => `/my/orders/${orderNo}`),
  MY_ORDERS_CANCEL: createRoute<{
    dynamicPath: { orderNo: string };
    searchParams?: { productId?: string; orderId?: string; redirect?: string };
  }>(({ orderNo }) => `/my/orders/${orderNo}/cancel`),
  MY_ORDERS_CANCEL_COMPLETE: createRoute<{
    dynamicPath: { orderNo: string };
  }>(({ orderNo }) => `/my/orders/${orderNo}/cancel/complete`),
  MY_ORDERS_RETURN: createRoute<{
    dynamicPath: { orderNo: string };
    searchParams?: { productId?: string; orderId?: string };
  }>(({ orderNo }) => `/my/orders/${orderNo}/return`),
  MY_ORDERS_REVIEWS: createRoute('/my/orders/reviews'),
  MY_ORDERS_REVIEW_DETAIL: createRoute<{
    dynamicPath: { reviewId: string };
  }>(({ reviewId }) => `/my/orders/reviews/${reviewId}`),
  MY_ORDERS_REVIEW_CREATE: createRoute<{
    dynamicPath: { orderNo: string };
    searchParams: { productId: string; orderId?: string; orderType?: string };
  }>(({ orderNo }) => `/my/orders/${orderNo}/reviews/create`),
  /** 배송지 관리 */
  MY_DELIVERY_ADDRESS: createRoute('/my/delivery-address'),

  RECOMMEND_POLICY_SITUATION_MAIN: createRoute<{
    dynamicPath: { caseType: POLICY_CASE_TYPE | 'ALL' };
  }>(({ caseType }) => `/recommend-policy/situation/main/${caseType}`),

  RECOMMEND_POLICY_SITUATION_EMERGENCY_FORM: createRoute(
    '/recommend-policy/situation/emergency',
  ),
  RECOMMEND_POLICY_SITUATION_EMERGENCY_HOSPITAL_LIST: createRoute(
    '/recommend-policy/situation/emergency/list',
  ),
  RECOMMEND_POLICY_SITUATION_EMERGENCY_MAP: createRoute(
    '/recommend-policy/situation/emergency/map',
  ),
  RECOMMEND_POLICY_SITUATION_EMERGENCY_DETAIL: createRoute<{
    dynamicPath: { id: number };
    searchParams: {
      symptomCode?: string;
      isEmergency?: boolean;
    };
  }>(({ id }) => `/recommend-policy/situation/emergency/${id}`),
  MEDICAL_POLICY_LIST: createRoute('/recommend-policy/medical'),

  PUSH: createRoute('/push'),

  MY_EDIT: createRoute<{
    searchParams: {
      redirect?: string;
    };
  }>('/my/edit'),

  MY_FAMILY: createRoute('/my/settings/family'),

  MY_FAMILY_UPDATE: createRoute<{
    dynamicPath: {
      updateType: FAMILY_PROFILE_UPDATE_PAGE_TYPE;
    };
    searchParams: {
      familyId?: number;
    };
  }>(({ updateType }) => `/my/settings/family/${updateType}`),

  MY_FAMILY_FILTER_UPDATE: createRoute<{
    dynamicPath: {
      updateType: FAMILY_PROFILE_UPDATE_PAGE_TYPE;
    };
    searchParams: {
      familyId?: number;
    };
  }>(({ updateType }) => `/my/settings/family/${updateType}/filter`),

  SEARCH: createRoute<{
    searchParams: {
      /** @description 검색어 */
      q?: string;
      category?:
        | 'all'
        | 'policy'
        | 'hometown-news'
        | 'policy-info'
        | 'gifts'
        | 'hometown-business'
        | 'welmarket';
    };
  }>('/search'),

  RECOMMEND_POLICY_LIST: createRoute<{
    searchParams: {
      filter?: 'available' | 'deadline' | 'new' | 'scrap';
      sort?: 'PRIORITY' | 'COMMENT' | 'SCRAP' | 'READ' | 'DUE_DATE_END';
      viewMode?: 'list' | 'timeline';
    };
  }>('/recommend-policy', {
    hasBottomNav: true,
  }),

  POLICY_DETAIL: createRoute<{
    dynamicPath: { idIdx: string };
  }>(({ idIdx }) => `/policy/${idIdx}`),

  /**@name 설정 */
  MY_SETTINGS: createRoute('/my/settings'),

  /**@name 알림설정 */
  MY_NOTIFICATION_SETTINGS: createRoute('/my/settings/notifications'),

  /**@name 계정연동 */
  MY_ACCOUNT_LINKING: createRoute('/my/settings/account-linking'),

  /**@name 공지사항*/
  MY_NOTICE: createRoute('/my/settings/notice'),

  /**@name 공지사항_상세 */
  MY_NOTICE_DETAIL: createRoute<{
    dynamicPath: { id: number };
  }>(({ id }) => `/my/settings/notice/${id}`),

  /**@name 자주_묻는_질문 */
  MY_FAQ: createRoute('/my/settings/faq'),

  /**@name 고객_문의 */
  MY_INQUIRY_LIST: createRoute('/my/settings/inquiry'),

  /**@name 문의_하기 */
  MY_INQUIRY_EDIT: createRoute<{
    searchParams: {
      category?: string;
    };
  }>('/my/settings/inquiry/register'),

  /**@name 문의_상세 */
  MY_INQUIRY_DETAIL: createRoute<{
    dynamicPath: { id: number };
  }>(({ id }) => `/my/settings/inquiry/${id}`),

  /**@name 회원탈퇴 */
  MY_RESIGN_MEMBER: createRoute('/my/settings/resign-member'),

  /** @name 맞춤_정책_필터_수정 */
  MY_POLICY_FILTER: createRoute('/my/edit/filter'),

  /**@name 장학재단 관련 */
  POLICY_KOSAF_APPLY: createRoute('/policy/apply/1'),
  POLICY_REPORT: createRoute('/policy/report'),

  POLICY_REPORT_DETAIL: createRoute<{
    searchParams: {
      /** @description 장학재단 신고 내역 상세 */
      report: string;
      reportYear: string;
      reportDate: string;
    };
  }>('/policy/report/detail'),

  /**@name 제주도 관련 */
  JEJU_APPLY: createRoute('/jeju/apply'),
  JEJU_REPORT: createRoute('/jeju/report'),
  JEJU_REPORT_DETAIL: createRoute<{
    dynamicPath: { id: number };
  }>(({ id }) => `/jeju/report/${id}`),

  POLICY_CONSENT: createRoute('/policy/consent'),
  POLICY_NOTICE: createRoute('/policy/notice'),
  POLICY_NOTICE_DETAIL: createRoute<{
    dynamicPath: { year: number };
  }>(({ year }) => `/policy/notice/${year}`),

  CROSS_DOMAIN_AUTH: createRoute<{
    searchParams: {
      accessToken: string;
      refreshToken: string;
    };
  }>('/login/auth/cross-domain'),

  /** AI 에이전트 */
  WELLO_AGENT: createRoute<{
    searchParams: {
      interestCompleted?: 'true';
    };
  }>('/wello-agent', {
    hasBottomNav: true,
  }),

  CROSS_DOMAIN_CALLBACK: createRoute<{
    searchParams: {
      accessToken: string;
      refreshToken: string;
      redirect?: string;
      redirectSearchParams?: string;
    };
  }>('/login/auth/cross-domain/callback'),

  JOIN_COMMON: createRoute<{
    searchParams: {
      step?: number;
    };
  }>('/join/common'),

  JOIN_COMPLETE: createRoute('/join/complete'),

  JOIN_USER_INFO: createRoute<{
    searchParams: {
      verificationId?: number;
    };
  }>('/join/user-info'),

  /** kb 전용 회원 가입 */
  KB_JOIN_COMMON: createRoute<{
    searchParams: {
      step?: number;
    };
  }>('/kb-join/common'),

  /** kb 전용 회원 가입 완료 */
  KB_JOIN_COMPLETE: createRoute('/kb-join/complete'),

  POLICY_FILTER: createRoute<{
    dynamicPath: { situation: string };
  }>(({ situation }) => `/filter/policy/${situation}`),

  INTEREST_POLICY_FILTER: createRoute<{
    dynamicPath: { situation: string };
  }>(({ situation }) => `/filter/policy/interest/${situation}`),

  KB_PAY_FILTER: createRoute('/filter/kb-pay'),

  TERMS: createRoute<{
    dynamicPath: { terms: TERM_CATEGORY };
  }>(({ terms }) => `/terms/${terms}`),

  TERMS_PRIVATE: createRoute<{
    dynamicPath: { terms: TERM_CATEGORY };
  }>(({ terms }) => `/my/settings/terms/${terms}`),

  EASY_CERTIFICATION: createRoute<{
    searchParams: EasyCertificationParams;
  }>('/easy-certification'),
  EASY_CERTIFICATION_CALLBACK: createRoute<{
    searchParams: EasyCertificationParams & {
      encryptMOKKeyToken?: string;
      serviceType?: string;
      txId?: string;
      usageCode?: EASY_LOGIN_USAGE_CODE;
    };
  }>('/easy-certification/callback'),

  ERROR: createRoute<{
    dynamicPath: { code: ERROR_CODE };
  }>(({ code }) => `/error/${code}`),

  NICE_CERTIFICATION: createRoute('/easy-certification/nice'),
  NICE_RESULT: createRoute<{
    dynamicPath: {
      result: 'pending' | 'success' | 'fail';
    };
  }>(({ result }) => `/easy-certification/nice/${result}`),
  CONTENT_COMPLIANCE_INFORMATION: createRoute(
    '/content-compliance-information',
  ),
  POINTS: createRoute('/points'),
  POINTS_OVERVIEW: createRoute('/points/overview'),
  POINTS_FORM: createRoute('/points/form'),
  POINTS_WITHDRAWAL_SUCCESS: createRoute('/points/withdrawal-success'),

  /**
   * @deprecated
   * 친구 초대
   */
  POINTS_INVITE: createRoute('/points/invite'),
  /** 웰포인트 유의사항 */
  POINTS_GUIDELINES: createRoute('/points/guidelines'),
  /** 매일 출석 체크 */
  ATTENDANCE_DAILY_CHECK: createRoute('/attendance/daily-check'),

  BENEFIT_NOTIFICATION: createRoute<{
    searchParams: {
      retry?: 'true';
    };
  }>('/benefit-notification'),
  BENEFIT_NOTIFICATION_AGREEMENT: createRoute(
    '/benefit-notification/agreement',
  ),
  BENEFIT_NOTIFICATION_CALLBACK: createRoute<{
    searchParams: {
      svcSeq: string;
      grpSeq: string;
      fixesGuidanceRequstSeq: string;
    };
  }>('/benefit-notification/callback'),

  INSPECTION: createRoute<{
    searchParams: {
      redirect?: string;
    };
    dynamicPath: {
      id: number;
    };
  }>(({ id }) => `/inspection/${id}`),

  POINT_MARKET: createRoute('/point-market'),
  POINT_MARKET_DETAIL: createRoute<{
    dynamicPath: { idx: string };
  }>(({ idx }) => `/point-market/${idx}`),
  POINT_MARKET_HISTORY: createRoute('/my/point-market'),
  POINT_MARKET_ORDER_COMPLETE: createRoute('/point-market/complete'),

  MEDIAN_INCOME: createRoute('/median-income', { hasBottomNav: true }),
} as const;

/** 고향 사랑 */
export const DONATION_ROUTES = {
  /** 메인 */
  HOME: createRoute('/donation', {
    hasBottomNav: true,
  }),
  /** 가이드 */
  GUIDE: createRoute('/donation/guide'),
  /** 고향 소개 상세 */
  HOMETOWNS_DETAIL: createRoute<{
    dynamicPath: { id: number };
    searchParams: {
      tab?: string;
      isDonation?: 'true';
      epayNo?: string;
      responseData?: string;
      forceHidePageNavigation?: 'true';
    };
  }>(({ id }) => `/donation/hometowns/${id}`, {
    hasBottomNav: true,
  }),
  /** 고향 소개 콘텐츠 디테일 */
  HOMETOWNS_CONTENTS_DETAIL: createRoute<{
    dynamicPath: { hometownsId: number; contentsId: number };
  }>(
    ({ hometownsId, contentsId }) =>
      `/donation/hometowns/${hometownsId}/contents/${contentsId}`,
  ),
  /** 답례품 */
  GIFTS: createRoute<{
    searchParams: {
      categoryId?: number;
      institutionId?: number;
      sort?: GiftProductSortType;
      isStockOnly?: boolean;
    };
  }>('/donation/gifts', {
    hasBottomNav: true,
    toastDirection: 'top',
  }),
  /** 답례품 검색 */
  GIFTS_SEARCH: createRoute<{
    searchParams: {
      searchKeyword?: string;
      category?: string;
    };
  }>('/donation/gifts/search'),
  /** 답례품 디테일 */
  GIFTS_DETAIL: createRoute<{
    dynamicPath: {
      productNumber: string;
    };
    searchParams?: {
      tab?: string;
      reviewSort?: GiftReviewSortType;
      detailReviewId?: number;
    };
  }>(({ productNumber }) => `/donation/gifts/${productNumber}`, {
    toastDirection: 'top',
  }),

  /** 지정기부 목록 */
  HOMETOWNS_BUSINESS: createRoute<{
    searchParams?: {
      sortType?: HometownBusinessSortType;
      institutionId?: number;
    };
  }>('/donation/hometown-business', {
    hasBottomNav: true,
  }),

  /** 지정기부 상세 */
  HOMETOWN_BUSINESS_DETAIL: createRoute<{
    dynamicPath: {
      id: number;
    };
    searchParams?: {
      tab?: string;
    };
  }>(({ id }) => `/donation/hometown-business/${id}`),

  /** 지정기부 주문 */
  HOMETOWN_BUSINESS_ORDER: createRoute<{
    searchParams?: {
      /** 지정사업 아이디 */
      donationBusinessId?: number;
      /** 지자체 아이디 */
      institutionId?: number;
      /** 지자체 이름 */
      institutionName?: string;
      /** 취소시 돌아갈 페이지 pathname */
      from?: string;
      /** prefill 기부 금액 */
      initialAmount?: number;
      /** prefill 기부 목적 */
      initialPurpose?: string;
    };
  }>('/donation/order'),

  /** 나의기부 */
  MY: createRoute('/donation/my', {
    hasBottomNav: true,
  }),

  /** 고객센터 - 기부내역 */
  MY_DONATIONS: createRoute<{
    searchParams?: {
      period?: number;
    };
  }>('/donation/my/donations'),

  /** 고객센터 - 주문내역 */
  DONATION_MY_ORDERS: createRoute('/donation/my/orders'),
  /** 고객센터 - 주문내역 - 상세 */
  DONATION_MY_ORDERS_DETAIL: createRoute<{
    dynamicPath: {
      id: string;
    };
  }>(({ id }) => `/donation/my/orders/${id}`),

  /** 고객센터 - 친구 초대 현황 */
  MY_INVITE: createRoute('/donation/my/invite'),

  /** 고객센터 - 탐나는 제주패스 - 공영 혜택 */
  MY_JEJU_BENEFIT_PUBLIC: createRoute('/donation/my/jeju/benefit/publics'),

  /** 고객센터 - 탐나는 제주패스 - 민영 혜택 */
  MY_JEJU_BENEFIT_COMMERCIAL: createRoute(
    '/donation/my/jeju/benefit/commercial',
  ),

  /** 고객센터 - 탐나는 제주패스 - 탐나는 제주패스란? */
  MY_JEJU_RECOMMEND_INFO: createRoute('/donation/my/jeju/recommend/info'),

  /** 고객센터 - 탐나는 제주패스 - 제주 혜택 지도 */
  MY_JEJU_RECOMMEND_MAP: createRoute('/donation/my/jeju/recommend/map'),

  /** 고객센터 - 1:1 문의 - 문의사항 선택 */
  MY_INQUIRY_PREPARE: createRoute('/donation/my/inquiry/prepare'),

  /** 고객센터 - 1:1 문의 - 문의내역 */
  MY_INQUIRY_HISTORY: createRoute('/donation/my/inquiry/history'),

  /** 고객센터 - 1:1 문의 - 문의 등록 */
  MY_INQUIRY_POSTING: createRoute<{
    searchParams?: {
      /** 주문/배송 문의 시 필수 */
      orderId?: string;
      /** 주문/배송 문의 시 필수 */
      orderProductId?: number;
      /** 답례품 문의 시 필수 */
      productNumber?: string;
      /** 문의 등록 후 이동할 페이지 */
      redirect?: string;
    };
    dynamicPath: {
      type: InquiryType;
    };
  }>(({ type }) => `/donation/my/inquiry/posting/${type}`),

  /** 고객센터 - 1:1 문의 - 문의 수정 */
  MY_INQUIRY_EDIT: createRoute<{
    searchParams?: {
      /** 문의 작성 후 이동할 페이지 */
      redirect?: string;
    };
    dynamicPath: {
      id: number;
    };
  }>(({ id }) => `/donation/my/inquiry/edit/${id}`),

  /** 고객센터 - 답례품 후기 */
  MY_REVIEW: createRoute('/donation/my/review'),

  /** 고객센터 - 답례품 후기 생성 */
  MY_REVIEW_CREATE: createRoute<{
    dynamicPath: {
      orderId: string;
    };
    searchParams: {
      redirect?: string;
      giftOrderProductId?: string;
    };
  }>(({ orderId }) => `/donation/my/review/create/${orderId}`),

  /** 고객센터 - 답례품 후기 수정 */
  MY_REVIEW_EDIT: createRoute<{
    dynamicPath: {
      reviewId: number;
    };
    searchParams: {
      redirect: string;
    };
  }>(({ reviewId }) => `/donation/my/review/${reviewId}/edit`),

  /** 고객센터 - 주문 취소/반품/교환 */
  DONATION_MY_ORDERS_RETURN_EXCHANGE: createRoute<{
    dynamicPath: { id: string };
  }>(({ id }) => `/donation/my/orders/${id}/return-exchange`),

  /** 고객센터 - 기타 - 공지사항 */
  MY_ETC_NOTICE: createRoute('/donation/my/etc/notice'),

  /** 고객센터 - 기타 - 공지사항 - 상세 */
  MY_ETC_NOTICE_DETAIL: createRoute<{
    dynamicPath: {
      noticeId: number;
    };
  }>(({ noticeId }) => `/donation/my/etc/notice/${noticeId}`),

  /** 고객센터 - 기타 - 이벤트 */
  MY_ETC_EVENT: createRoute('/donation/my/etc/event'),

  /** 고객센터 - 기타 - 이벤트 - 상세 */
  MY_ETC_EVENT_DETAIL: createRoute<{
    dynamicPath: {
      eventId: number;
    };
  }>(({ eventId }) => `/donation/my/etc/event/${eventId}`),

  /** 기부 결제 완료 콜백 */
  CALLBACK: createRoute<{
    dynamicPath: {
      epayNo: string;
    };
    searchParams?: {
      subDomain?: string;
      osType?: string;
    };
  }>(({ epayNo }) => `/donation/callback/${epayNo}`),

  //* 이벤트 관련 임시 라우팅
  /** 트리플 혜택 */
  TRIPLE_BENEFIT: createRoute('/donation/triple-benefit'),
  /** 갤럭시 8 기브 */
  GALAXY_WATCH: createRoute('/donation/galaxy-watch'),
  /** KB국민카드 체크카드 이벤트 */
  KB_CHECK_CARD: createRoute('/donation/event/kb-check-card'),

  /** 이달의 기획전 상세 */
  MONTHLY_PROMOTION: createRoute('/donation/monthly-promotion'),
  /** 제주도 기부 이벤트 - 돌하르방 */
  STONE_GRANDFATHER: createRoute('/donation/stone-grandfather'),
  /** 친구 초대 페이지 */
  DONATION_INVITE: createRoute('/donation/invite'),
  /** 친구 초대 페이지 - 초대받은사람이 초대링크를 타고 들어온 페이지 */
  DONATION_INVITE_RECEIVER: createRoute<{
    dynamicPath: {
      //* 초대를 하는 회원의 멤버코드
      memberCode: string;
    };
  }>(({ memberCode }) => `/donation/invite/${memberCode}`),
  /** 친구 초대 페이지 - 선물상자 열기 */
  MY_BOX: createRoute('/donation/my/box'),

  /** 전국 스탬프 이벤트 페이지 */
  STAMP: createRoute('/donation/event/stamp'),

  /** 연말기념 산타박스 이벤트 페이지 */
  MARKET: createRoute('/donation/event/market'),
  /** 친구 초대 이벤트 실시간 랭킹 */
  MARKET_INVITE: createRoute('/donation/event/market/invite'),

  SNS_MARKET: createRoute('/donation/event/market/sns'),

  /** 장바구니 */
  CART: createRoute('/donation/cart', {
    toastDirection: 'top',
  }),
  CART_ORDER: createRoute('/donation/cart/order'),
} as const;

export const WEL_MARKET_ROUTES = {
  HOME: createRoute('/welmarket', {
    hasBottomNav: true,
  }),
  PRODUCT_DETAIL: createRoute<{
    dynamicPath: {
      productNumber: string;
    };
  }>(({ productNumber }) => `/welmarket/products/${productNumber}`, {
    toastDirection: 'top',
  }),
  GIFT_CARD_DETAIL: createRoute<{
    dynamicPath: {
      id: string;
    };
  }>(({ id }) => `/welmarket/gift-cards/${id}`, {
    toastDirection: 'top',
  }),
  GIFT_CARD_ORDER: createRoute<{
    dynamicPath: {
      id: string;
    };
  }>(({ id }) => `/welmarket/gift-cards/${id}/order`),
  BRAND: createRoute('/welmarket/brand'),
  SPECIALTIES: createRoute('/welmarket/specialties', {
    hasBottomNav: true,
  }),
  MOBILE_COUPONS: createRoute('/welmarket/mobile-coupons', {
    hasBottomNav: true,
  }),
  CART: createRoute('/welmarket/cart', {
    hasBottomNav: false,
    toastDirection: 'top',
  }),
  ORDER: createRoute<{
    dynamicPath: { beforeOrderId: string };
  }>(({ beforeOrderId }) => `/welmarket/order/${beforeOrderId}`),
  ORDER_COMPLETE: createRoute('/welmarket/order/complete'),
  GIFT_CARD_ORDER_COMPLETE: createRoute('/welmarket/gift-cards/order-complete'),
} as const;

export const API_ROUTES = {
  WELLO_AGENT_SESSIONS: createRoute('/api/wello-agent/sessions'),
  WELLO_AGENT_CHAT_COMPLETIONS: createRoute(
    '/api/wello-agent/chat/completions',
  ),
  WELLO_AGENT_CHAT_COMPLETIONS_STREAM: createRoute<{
    searchParams: {
      session_id: string;
      message: string;
      current_policy_id?: string;
      user_id?: string;
    };
  }>('/api/wello-agent/chat/completions/stream'),

  NAVER_AUTH: createRoute('/api/auth/naver'),
  GOOGLE_AUTH: createRoute('/api/auth/google'),
  APPLE_AUTH: createRoute('/api/auth/apple'),
  APPLE_TOKEN: createRoute('/api/auth/apple/token'),
  TOSS_AUTH: createRoute('/api/auth/toss'),
  MOBILE_OK_REQUEST: createRoute<{
    searchParams: EasyCertificationParams & {
      usageCode: EASY_LOGIN_USAGE_CODE;
    };
  }>('/api/mobile-ok'),
  MOBILE_OK_CALLBACK: createRoute<{
    searchParams: EasyCertificationParams & {
      usageCode: EASY_LOGIN_USAGE_CODE;
      txId: string;
      serviceType: VERIFICATION_SERVICE_TYPE;
    };
  }>('/api/mobile-ok/callback'),
  IOK_REQUEST: createRoute<{
    searchParams: EasyCertificationParams & {
      usageCode: EASY_LOGIN_USAGE_CODE;
      authProviderCode: string;
    };
  }>('/api/iok'),
  IOK_CALLBACK: createRoute<{
    searchParams: EasyCertificationParams & {
      usageCode: EASY_LOGIN_USAGE_CODE;
      txId: string;
      serviceType: VERIFICATION_SERVICE_TYPE;
    };
  }>('/api/iok/callback'),
  JEJU_APP_API: createRoute('/api/jeju-app'),
  BENEFIT_NOTIFICATION: createRoute<{
    dynamicPath: { dynamicPath: string };
    searchParams?: {
      svcSeq?: string;
      grpSeq?: string;
      fixesGuidanceRequstSeq?: string;
      osType?: OsType;
      accessToken: string | null;
      refreshToken: string | null;
      isWelloApp?: boolean;
      isKbWebview?: boolean;
    };
  }>(({ dynamicPath }) => `/api/benefit-notification/${dynamicPath}`),
} as const;

export const MODULE_ROUTES = {
  KB_HOME: createRoute('/kb-home', {
    hasBottomNav: true,
  }),
};

/** /donation/** 이외의 반응형 페이지 */
